import { type Point } from "./models";

export class PolygonBoundaryGenerator {
    private static readonly EARTH_RADIUS_M = 6371000; // Earth's radius in meters

    /**
     * Creates a boundary polygon around an existing polygon, always on the outside
     * @param innerPolygon Array of points representing the inner polygon
     * @param bufferDistanceM Buffer distance in meters
     * @returns Array of points representing the boundary polygon
     */
    static createBoundaryPolygon(innerPolygon: Point[], bufferDistanceM: number): Point[] {
        if (innerPolygon.length < 3) {
            throw new Error('Inner polygon must have at least 3 points');
        }

        const boundaryPoints: Point[] = [];
        // Determine winding direction to ensure outward offset
        const isClockwise = this.isPolygonClockwise(innerPolygon);

        for (let i = 0; i < innerPolygon.length; i++) {
            const currentPoint = innerPolygon[i];
            const prevPoint = innerPolygon[i === 0 ? innerPolygon.length - 1 : i - 1];
            const nextPoint = innerPolygon[(i + 1) % innerPolygon.length];

            // Calculate the outward offset point for this vertex
            const offsetPoint = this.calculateOffsetPoint(
                currentPoint,
                prevPoint,
                nextPoint,
                bufferDistanceM,
                isClockwise
            );

            boundaryPoints.push(offsetPoint);
        }

        return boundaryPoints;
    }

    /**
     * Determine if the polygon is wound clockwise or counterclockwise
     * @param polygon Array of points
     * @returns True if clockwise, false if counterclockwise
     */
    private static isPolygonClockwise(polygon: Point[]): boolean {
        let signedArea = 0;
        for (let i = 0; i < polygon.length; i++) {
            const current = polygon[i];
            const next = polygon[(i + 1) % polygon.length];
            signedArea += (current.longitude * next.latitude) - (next.longitude * current.latitude);
        }
        // Negative area indicates clockwise winding
        return signedArea < 0;
    }

    /**
     * Calculate offset point for a vertex considering its adjacent vertices
     * @param isClockwise Whether the polygon is wound clockwise
     */
    private static calculateOffsetPoint(
        current: Point,
        prev: Point,
        next: Point,
        distance: number,
        isClockwise: boolean
    ): Point {
        // Calculate vectors from current point to adjacent points
        const vec1 = this.normalizeVector(this.getVector(prev, current));
        const vec2 = this.normalizeVector(this.getVector(current, next));

        // Calculate the bisector (average of the two unit vectors)
        const bisector = {
            x: (vec1.x + vec2.x) / 2,
            y: (vec1.y + vec2.y) / 2
        };

        // Normalize the bisector
        const normalizedBisector = this.normalizeVector(bisector);

        // Calculate the perpendicular vector (rotate 90 degrees)
        // For counterclockwise, rotate 90° clockwise for outward
        // For clockwise, rotate 90° counterclockwise for outward
        const perpendicular = isClockwise
            ? { x: -normalizedBisector.y, y: normalizedBisector.x } // Counterclockwise rotation
            : { x: normalizedBisector.y, y: -normalizedBisector.x }; // Clockwise rotation

        // Convert distance from meters to degrees (approximate)
        const latOffset = (distance * perpendicular.y) / this.EARTH_RADIUS_M * (180 / Math.PI);
        const lngOffset =
            (distance * perpendicular.x) /
            (this.EARTH_RADIUS_M * Math.cos(current.latitude * Math.PI / 180)) * (180 / Math.PI);

        return {
            latitude: current.latitude + latOffset,
            longitude: current.longitude + lngOffset
        };
    }

    /**
     * Create a vector between two points
     */
    private static getVector(from: Point, to: Point): { x: number, y: number } {
        return {
            x: to.longitude - from.longitude,
            y: to.latitude - from.latitude
        };
    }

    /**
     * Normalize a vector to unit length
     */
    private static normalizeVector(vector: { x: number, y: number }): { x: number, y: number } {
        const length = Math.sqrt(vector.x * vector.x + vector.y * vector.y);
        if (length === 0) {
            return { x: 0, y: 0 };
        }
        return {
            x: vector.x / length,
            y: vector.y / length
        };
    }
}